import React from "react";
import { Box } from "@mui/material";
import { dt } from "../../../shared/themes/redesignTokens";
import { midasDotLit, liveDotVisual, totalDocs, ReadinessVM } from "../utils/midasReadiness";
import useTranslation from "../../../shared/hooks/useTranslation";

// =====================================================================
//  CatalogReadinessRow (DR-2) — MIDAS / LIVE / OEM dot cluster + doc count.
//  Present on EVERY catalog card (unlit for no-data models). Exact colors
//  and spacing pulled from Paper artboard v2-1 (get_jsx of all 4 cards).
//
//  Dot visual states (from the artboard):
//   - lit    : solid fill in the signal colour (green LIVE also gets a glow ring)
//   - hollow : ring outline #B9BEC6 — LIVE only, "possible / unconfirmed"
//   - gray   : solid faint #DADEE3 — signal absent / no data
//
//  The "View details ›" trigger is intentionally NOT rendered here — it is
//  wired to the shared popup in DR-3, as a sibling row below this one.
// =====================================================================

// Exact tokens from Paper artboard v2-1
const rd = {
  litMidasDot: "#3498DB",
  litMidasText: "#2E86DE",
  liveGreenDot: "#27AE60",
  liveGreenText: "#27AE60",
  liveGreenGlow: "#27AE602E", // 0 0 0 2px ring
  hollowBorder: "#B9BEC6",
  hollowText: "#9AA0A8",
  oemGoldDot: "#C9962E",
  oemGoldText: "#A87F1E",
  grayDot: "#DADEE3",
  grayText: "#C4C8CE",
  docCountText: "#9AA0A8",
  noDataText: "#C4C8CE",
};

type DotVariant =
  | { kind: "lit"; color: string; glow?: string; labelColor: string }
  | { kind: "hollow"; labelColor: string }
  | { kind: "gray" };

const ReadinessDot: React.FC<{ label: string; variant: DotVariant }> = ({ label, variant }) => {
  const dotSx =
    variant.kind === "lit"
      ? {
          backgroundColor: variant.color,
          boxShadow: variant.glow ? `0 0 0 2px ${variant.glow}` : "none",
        }
      : variant.kind === "hollow"
        ? { backgroundColor: "transparent", border: `1.3px solid ${rd.hollowBorder}` }
        : { backgroundColor: rd.grayDot };

  const labelColor =
    variant.kind === "gray" ? rd.grayText : (variant as { labelColor: string }).labelColor;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
      <Box
        sx={{
          width: "6px",
          height: "6px",
          borderRadius: "50%",
          flexShrink: 0,
          ...dotSx,
        }}
      />
      <Box
        component="span"
        sx={{
          fontSize: "10px",
          lineHeight: "12px",
          fontWeight: 600,
          fontFamily: dt.font,
          letterSpacing: "0.04em",
          color: labelColor,
        }}
      >
        {label}
      </Box>
    </Box>
  );
};

// Structural subset — accepts both a CatalogModelListItem and a
// CatalogModelDetail (item 5) since the row only needs these two fields.
interface CatalogReadinessRowProps {
  model: {
    readiness?: ReadinessVM | null;
    isOemPartner?: boolean;
  };
}

const CatalogReadinessRow: React.FC<CatalogReadinessRowProps> = ({ model }) => {
  const { translate } = useTranslation("myMachinery");

  const readiness = model.readiness;
  const hasReadiness = !!readiness;
  const midasLit = midasDotLit(readiness);
  const liveVis = liveDotVisual(readiness); // no ctx — catalog has no instance connection
  const isOem = model.isOemPartner === true;
  const total = totalDocs(readiness);

  const midasVariant: DotVariant = midasLit
    ? { kind: "lit", color: rd.litMidasDot, labelColor: rd.litMidasText }
    : { kind: "gray" };

  // LIVE: lit(green — proven/connected) → hollow(possible OR has-data-but-unknown) → gray(unlit/no data)
  const liveVariant: DotVariant =
    liveVis === "lit"
      ? { kind: "lit", color: rd.liveGreenDot, glow: rd.liveGreenGlow, labelColor: rd.liveGreenText }
      : liveVis === "hollow"
        ? { kind: "hollow", labelColor: rd.hollowText }
        : { kind: "gray" };

  const oemVariant: DotVariant = isOem
    ? { kind: "lit", color: rd.oemGoldDot, labelColor: rd.oemGoldText }
    : { kind: "gray" };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "9px" }}>
      <ReadinessDot label="MIDAS" variant={midasVariant} />
      <ReadinessDot label="LIVE" variant={liveVariant} />
      <ReadinessDot label="OEM" variant={oemVariant} />
      <Box
        component="span"
        sx={{
          flex: 1,
          textAlign: "right",
          fontSize: "10px",
          lineHeight: "12px",
          fontFamily: dt.font,
          color: hasReadiness ? rd.docCountText : rd.noDataText,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {hasReadiness ? translate("docsCount", { count: total }) : translate("noDataYet")}
      </Box>
    </Box>
  );
};

export default CatalogReadinessRow;
